import { initLazyScript } from '../utils/theme-functions';

class DetailsDisclosure extends HTMLElement {
    constructor() {
        super();

        initLazyScript(this, this.init.bind(this));
    }

    init() {
        this.disclosure = this.querySelector('details');
        this.toggle = this.querySelector('summary');
        this.panel = this.toggle.nextElementSibling;

        // Check if the content element has a CSS transition.
        if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
            this.boundHandleToggle = this.handleToggle.bind(this);
            this.boundHandleTransitionEnd = this.handleTransitionEnd.bind(this);

            this.toggle.addEventListener('click', this.boundHandleToggle);
            this.disclosure.addEventListener('transitionend', this.boundHandleTransitionEnd);
        }
    }

    connectedCallback() {
        // Minimal connection logic if needed
    }

    disconnectedCallback() {
        // Cleanup event listeners to prevent memory leaks
        if (this.toggle && this.boundHandleToggle) {
            this.toggle.removeEventListener('click', this.boundHandleToggle);
        }
        if (this.disclosure && this.boundHandleTransitionEnd) {
            this.disclosure.removeEventListener('transitionend', this.boundHandleTransitionEnd);
        }
    }

    /**
     * Handles 'click' events on the summary element.
     * @param {object} evt - Event object.
     */
    handleToggle(evt) {
        evt.preventDefault();
        evt.stopPropagation();

        if (!this.disclosure.open) {
            this.open();
        } else {
            this.close();
        }
    }

    /**
     * Handles 'transitionend' events on the details element.
     * @param {object} evt - Event object.
     */
    handleTransitionEnd(evt) {
        if (evt.target !== this.panel) return;

        if (this.disclosure.classList.contains('is-closing')) {
            this.disclosure.classList.remove('is-closing');
            this.disclosure.open = false;
        }

        this.panel.removeAttribute('style');
    }

    /**
     * Adds inline 'height' style to the content element, to trigger open transition.
     */
    addContentHeight() {
        this.panel.style.height = `${this.panel.scrollHeight}px`;
    }

    /**
     * Opens the details element.
     */
    open() {
        // Set content 'height' to zero before opening the details element.
        this.panel.style.height = '0';

        // Open the details element
        this.disclosure.open = true;

        // Set content 'height' to its scroll height, to enable CSS transition.
        this.addContentHeight();
    }

    /**
     * Closes the details element.
     */
    close() {
        // Prevent multiple close operations
        if (this.disclosure.classList.contains('is-closing')) {
            return;
        }

        // Set content height to its scroll height, to enable transition to zero.
        this.addContentHeight();

        // Add class to enable styling of content or toggle icon before or during close transition.
        this.disclosure.classList.add('is-closing');

        // Use requestAnimationFrame for better timing instead of arbitrary timeout
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.panel.style.height = '0';
            });
        });
    }
}

customElements.define('details-disclosure', DetailsDisclosure);
